import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Plus, Save, Loader2, Edit, Trash2, Eye, MessageSquare } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { formatDateTime } from '../../../lib/dateFormat';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormLabel } from '../../../components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { smsTemplatesApi, type SmsTemplateDto, templateVariablesApi } from '../../../lib/api';
import { smsTemplatesPreviewApi, type VorschauDatensatz } from '../../../lib/api/templates';
import VorschauDatensatzWahl from './VorschauDatensatzWahl';
import { Badge } from '../../../components/ui/badge';
import {
  GeltungAuswahl,
  GeltungHinweis,
  useStandortOptionen,
  useStandortVorlagen,
  type VorlagenEbene,
} from './standortGeltung';

function extractPlaceholders(s: string): string[] {
  const out: string[] = [];
  const re = /\{\{([^}]+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s || ''))) {
    const v = (m[1] || '').trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out.sort();
}

const SmsTemplates = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState<'edit' | 'preview'>('edit');
  const [templateKey, setTemplateKey] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templates, setTemplates] = useState<SmsTemplateDto[]>([]);
  const [zuLoeschen, setZuLoeschen] = useState<SmsTemplateDto | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplateDto | null>(null);
  const [allowedVars, setAllowedVars] = useState<string[]>([]);
  const bodyEditorRef = useRef<HTMLTextAreaElement | null>(null);

  // Wofür gepflegt wird: global (null) oder ein Standort — wie bei den E-Mail-Vorlagen.
  const standorte = useStandortOptionen();
  const [geltung, setGeltung] = useState<number | null>(null);
  const [ebene, setEbene] = useState<VorlagenEbene>('global');
  const geltungName = standorte.find((s) => s.id === geltung)?.name ?? '';
  const { vorlagen: standortVorlagen, merken, entfernen } = useStandortVorlagen(geltung, smsTemplatesApi.list);

  const createQuickTemplate = async (key: string) => {
    try {
      const exists = templates.some((t) => t.template_key === key);
      if (exists) {
        toast.info('Template existiert bereits');
        return;
      }
      const defaultBody =
        key === 'communications.manual.trial_request'
          ? 'Hallo {{studentName}}, … Viele Grüße {{schoolName}}'
          : 'Hallo {{recipientName}}, … Viele Grüße {{schoolName}}';
      const created = await smsTemplatesApi.create({ template_key: key, body: defaultBody });
      setTemplates((prev) => [created, ...prev]);
      toast.success('Template angelegt');
    } catch (e: any) {
      toast.error(e?.message || 'Anlegen fehlgeschlagen');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const tpls = await smsTemplatesApi.list();
        setTemplates(tpls);
      } catch (e: any) {
        toast.error(e?.message || 'Fehler beim Laden der SMS-Vorlagen');
      }
    };
    load();
  }, []);

  const filteredTemplates = templates.filter(t =>
    t.template_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.body || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const usedVars = useMemo(() => extractPlaceholders(templateBody), [templateBody]);
  const unknownVars = useMemo(() => usedVars.filter(v => !allowedVars.includes(v)), [usedVars, allowedVars]);
  const characterCount = templateBody.length;
  const smsSegments = Math.max(1, Math.ceil(characterCount / 160));
  let countToneClass = 'text-muted-foreground';
  if (characterCount >= 320) {
    countToneClass = 'text-destructive';
  } else if (characterCount >= 160) {
    countToneClass = 'text-amber-600';
  }

  // Vorschau vom Server: Beispielwerte aus dem Katalog oder ein echter Datensatz (T-0180).
  const [vorschauDatensatz, setVorschauDatensatz] = useState<VorschauDatensatz | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const vorschauKey = selectedTemplate?.template_key || templateKey;
  const renderPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await smsTemplatesPreviewApi.render({
        template_key: vorschauKey,
        body: templateBody,
        standort_id: geltung,
        ...(vorschauDatensatz ?? {}),
      });
      setPreviewText(res.text);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Vorschau konnte nicht gerendert werden');
    } finally {
      setPreviewLoading(false);
    }
  };
  useEffect(() => {
    if (dialogTab === 'preview') void renderPreview();
    // Beim Öffnen der Vorschau und bei anderem Datensatz neu rendern.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogTab, vorschauDatensatz]);

  const insertTokenAtCursor = (
    token: string,
    ref: React.RefObject<HTMLTextAreaElement | null>,
    value: string,
    setValue: (next: string) => void
  ) => {
    const el = ref.current;
    if (!el) {
      setValue(value + token);
      return;
    }
    const start = typeof el.selectionStart === 'number' ? el.selectionStart : value.length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
    const next = value.slice(0, start) + token + value.slice(end);
    setValue(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch {
        // noop
      }
    });
  };

  const handleSaveTemplate = async () => {
    setIsLoading(true);
    try {
      const key = templateKey.trim();
      if (!key) throw new Error('Template-Key ist erforderlich');
      if (selectedTemplate?.template_key && ebene !== 'global' && geltung !== null) {
        const gespeichert = ebene === 'standort'
          ? await smsTemplatesApi.update(key, { body: templateBody }, geltung)
          : await smsTemplatesApi.create({ template_key: key, body: templateBody, standort_id: geltung });
        merken(gespeichert);
        toast.success(`Vorlage für ${geltungName} gespeichert`);
      } else if (selectedTemplate?.template_key) {
        const updated = await smsTemplatesApi.update(selectedTemplate.template_key, { body: templateBody });
        setTemplates(prev => prev.map(t => t.template_key === updated.template_key ? updated : t));
        toast.success(geltung === null ? 'SMS-Vorlage wurde erfolgreich gespeichert' : 'Globale Vorlage gespeichert');
      } else {
        const created = await smsTemplatesApi.create({ template_key: key, body: templateBody });
        setTemplates(prev => [created, ...prev]);
        toast.success('SMS-Vorlage wurde erfolgreich angelegt');
      }
      setIsDialogOpen(false);
    } catch (error: any) {
      toast.error(error?.message || 'Fehler beim Speichern der SMS-Vorlage');
    } finally {
      setIsLoading(false);
    }
  };

  const loadVars = async (key: string) => {
    try {
      const vars = await templateVariablesApi.get(key);
      setAllowedVars(vars || []);
    } catch {
      setAllowedVars([]);
    }
  };

  const handleEditTemplate = (template: SmsTemplateDto) => {
    // Gibt es für den gewählten Standort eine eigene Vorlage, wird die bearbeitet.
    const eigene = geltung !== null ? standortVorlagen[template.template_key] : undefined;
    setEbene(eigene ? 'standort' : 'global');
    setSelectedTemplate(template);
    setTemplateKey(template.template_key || '');
    setTemplateBody((eigene ?? template).body || '');
    setDialogTab('edit');
    setIsDialogOpen(true);
    void loadVars(template.template_key);
  };

  const handleCreateTemplate = () => {
    setEbene('global');
    setSelectedTemplate(null);
    setTemplateKey('');
    setTemplateBody('');
    setAllowedVars([]);
    setDialogTab('edit');
    setIsDialogOpen(true);
  };

  useEffect(() => {
    const key = templateKey.trim();
    if (!key) {
      setAllowedVars([]);
      return;
    }
    void loadVars(key);
  }, [templateKey]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">SMS-Vorlagen</h1>
          <p className="text-muted-foreground">Verwalten Sie SMS-/Copy-Text Vorlagen für Benachrichtigungen</p>
        </div>
        <Button onClick={handleCreateTemplate}><Plus className="mr-2 h-4 w-4" />Neue Vorlage</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Create: Nachrichten-Modal</CardTitle>
          <CardDescription>Legt Standard-Templates für das universelle Nachrichten-Modal an (falls noch nicht vorhanden).</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void createQuickTemplate('communications.manual.person')}>
            communications.manual.person
          </Button>
          <Button variant="outline" onClick={() => void createQuickTemplate('communications.manual.course.participants')}>
            communications.manual.course.participants
          </Button>
          <Button variant="outline" onClick={() => void createQuickTemplate('communications.manual.trial_request')}>
            communications.manual.trial_request
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <GeltungAuswahl standorte={standorte} geltung={geltung} onChange={setGeltung} />
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input uiSize="md" placeholder="Vorlagen durchsuchen..." className="pl-8" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>SMS-Vorlagen</CardTitle>
          <CardDescription>Eine Liste aller verfügbaren SMS-Vorlagen</CardDescription>
        </CardHeader>
        <CardContent>
          <Table density="compact">
            <TableHeader>
              <TableRow>
                <TableHead density="compact">Key</TableHead>
                <TableHead density="compact">Status</TableHead>
                {geltung !== null ? <TableHead density="compact">Gilt in {geltungName}</TableHead> : null}
                <TableHead density="compact">Zuletzt bearbeitet</TableHead>
                <TableHead density="compact" className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.template_key}>
                  <TableCell density="compact" className="font-medium">{template.template_key}</TableCell>
                  <TableCell density="compact">
                    <Badge size="sm" variant={Number(template.is_active) ? 'success' : 'secondary'}>
                      {Number(template.is_active) ? 'aktiv' : 'inaktiv'}
                    </Badge>
                  </TableCell>
                  {geltung !== null ? (
                    <TableCell density="compact">
                      {standortVorlagen[template.template_key] ? (
                        <Badge size="sm" variant="default">eigene Vorlage</Badge>
                      ) : (
                        <Badge size="sm" variant="secondary">globale Vorlage</Badge>
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell density="compact">{formatDateTime(template.updated_at)}</TableCell>
                  <TableCell density="compact" className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                      <Edit className="h-4 w-4" /><span className="sr-only">Bearbeiten</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setZuLoeschen(template)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                      <span className="sr-only">Löschen</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell density="compact" colSpan={geltung !== null ? 5 : 4} className="h-24 text-center">Keine Vorlagen gefunden.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? 'SMS-Vorlage bearbeiten' : 'Neue SMS-Vorlage erstellen'}</DialogTitle>
            <DialogDescription>Erstellen oder aktualisieren Sie eine SMS-/Copy-Text Vorlage.</DialogDescription>
          </DialogHeader>
          <Tabs value={dialogTab} onValueChange={(v) => setDialogTab(v as any)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="edit"><Edit className="mr-2 h-4 w-4" />Bearbeiten</TabsTrigger>
              <TabsTrigger value="preview"><Eye className="mr-2 h-4 w-4" />Vorschau</TabsTrigger>
            </TabsList>
            <TabsContent value="edit" className="space-y-4">
              <div className="grid gap-4 py-4">
                {selectedTemplate ? (
                  <GeltungHinweis
                    geltung={geltung}
                    geltungName={geltungName}
                    ebene={ebene}
                    onEigeneAnlegen={() => setEbene('standort_neu')}
                  />
                ) : null}
                <div className="space-y-2">
                  <FormLabel htmlFor="template-key">Template-Key</FormLabel>
                  <Input
                    id="template-key"
                    uiSize="md"
                    value={templateKey}
                    onChange={e => setTemplateKey(e.target.value)}
                    placeholder="z.B. trial.request.hidden.not_reachable"
                    disabled={!!selectedTemplate}
                  />
                </div>

                <div className="space-y-2">
                  <FormLabel htmlFor="template-body">Inhalt</FormLabel>
                  <Textarea
                    ref={bodyEditorRef}
                    id="template-body"
                    density="comfortable"
                    rows={10}
                    value={templateBody}
                    onChange={e => setTemplateBody(e.target.value)}
                    placeholder="Hallo {{studentName}}, …"
                  />
                  <div className={`text-xs ${countToneClass}`}>
                    {characterCount} Zeichen · ca. {smsSegments} SMS-Segment{smsSegments === 1 ? '' : 'e'} (je 160)
                  </div>
                </div>

                <div className="rounded-md border p-3 text-sm">
                  <div className="font-medium mb-2 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Variablen</div>
                  <div className="text-xs text-muted-foreground mb-2">Erlaubt für dieses Template-Key (via `template-variables`):</div>
                  <div className="flex flex-wrap gap-2">
                    {allowedVars.length === 0 ? (
                      <span className="text-xs text-muted-foreground">Keine Variablen definiert</span>
                    ) : allowedVars.map(v => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => insertTokenAtCursor(`{{${v}}}`, bodyEditorRef, templateBody, setTemplateBody)}
                        className="cursor-pointer"
                      >
                        <Badge size="sm" variant="secondary">{`{{${v}}}`}</Badge>
                      </button>
                    ))}
                  </div>
                  {unknownVars.length > 0 ? (
                    <div className="mt-3 text-xs text-destructive">
                      Unbekannte Variablen im Inhalt: {unknownVars.map(v => `{{${v}}}`).join(', ')}
                    </div>
                  ) : null}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="preview" className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Vorschau</CardTitle>
                  <CardDescription>Ohne Datensatz stehen Beispielwerte in den Platzhaltern.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <VorschauDatensatzWahl
                    templateKey={vorschauKey}
                    standortId={geltung}
                    value={vorschauDatensatz}
                    onChange={setVorschauDatensatz}
                  />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => void renderPreview()} disabled={previewLoading}>
                      {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                      Vorschau aktualisieren
                    </Button>
                  </div>
                  <div className="rounded-md border p-4 whitespace-pre-wrap text-sm">{previewText || <span className="text-muted-foreground">(Kein Inhalt)</span>}</div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveTemplate} disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={
          geltung !== null && zuLoeschen && standortVorlagen[zuLoeschen.template_key]
            ? `Eigene Vorlage von ${geltungName} entfernen?`
            : `Vorlage „${zuLoeschen?.template_key ?? ''}“ deaktivieren?`
        }
        beschreibung={
          geltung !== null && zuLoeschen && standortVorlagen[zuLoeschen.template_key]
            ? 'Danach gilt an diesem Standort wieder die globale Vorlage. Der Grund wird im Protokoll festgehalten.'
            : 'Die Vorlage wird deaktiviert und steht danach nicht mehr zur Auswahl. Der Grund wird im Protokoll festgehalten.'
        }
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (!zuLoeschen) return;
          const key = zuLoeschen.template_key;
          if (geltung !== null && standortVorlagen[key]) {
            await smsTemplatesApi.delete(key, grund, geltung);
            entfernen(key);
            setZuLoeschen(null);
            toast.success(`Eigene Vorlage von ${geltungName} entfernt`);
            return;
          }
          await smsTemplatesApi.delete(key, grund);
          setTemplates((prev) => prev.map((t) => (t.template_key === key ? { ...t, is_active: 0 } : t)));
          setZuLoeschen(null);
          toast.success('Vorlage wurde deaktiviert');
        }}
      />
    </div>
  );
};

export default SmsTemplates;


